import dedent from 'ts-dedent'
import {
  ColumnDataType,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType
} from '../types'
import { createTableWithBBox, getSetClusterParamsQuery } from '../utils/databricks'
import { DBricksConnection } from '../workflow/connections/databricks'
import { splitPairs } from './common/enrichcomponent'
import { EnrichComponentDatabricks } from './common/enrichcomponent_db'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

class EnrichPolygonsWeightedDatabricks extends EnrichComponentDatabricks {
  constructor() {
    super(
      {
        name: 'native.enrichpolygonsweighted',
        group: 'Data Enrichment',
        title: 'Enrich Polygons with Weights',
        description: `
      This uses a data source to enrich another target table using weights to control the enrichment.
      `,
        starred: true
      },
      'polygon',
      [ColumnDataType.Geography, ColumnDataType.Geometry],
      (x) => `${x}`,
      true
    )
    this.inputs.push({
      name: 'weights_table',
      type: WorkflowComponentParamType.InputTable,
      title: 'Weights table',
      description: 'Source for the weights',
      placeholder: 'FQN of the weights table'
    })

    this.inputs.push({
      name: 'weights_col',
      type: WorkflowComponentParamType.Column,
      title: 'Weights geo column',
      parent: 'weights_table',
      dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
    })

    this.inputs.push({
      name: 'weights',
      type: WorkflowComponentParamType.SelectColumnAggregation,
      title: 'Weights column',
      parent: 'weights_table',
      noDefault: true,
      doVariables: true,
      description:
        'Comma-separated list of variable names and aggregation methods (e.g. population_93405ad7, sum, population_93405ad7, avg)',
      placeholder: 'population_93405ad7, sum, population_93405ad7, avg',
      optional: false
    })
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams): WorkflowParameterValueError[] {
    const errors = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })

    const preparedParams = this._prepareParameters(resolvedParamValues)

    if (preparedParams.weights == null) {
      errors.push({
        parameter: 'weights',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'Weight column must be provided'
      })
    } else if ((preparedParams.weights as string).split(',').length < 2) {
      errors.push({
        parameter: 'weights',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'At least a variable and an aggregation method must be provided'
      })
    } else if ((preparedParams.weights as string).split(',').length > 2) {
      errors.push({
        parameter: 'weights',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'Only one weight column is allowed'
      })
    }

    return errors
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    const prepParams = this._prepareParameters(params)
    const target_col = prepParams.target_col as string
    const variables = prepParams.variables as string
    const variablePairs = splitPairs(variables?.split(',')?.map((x) => x?.trim()))
    const weight = splitPairs((prepParams.weights as string).split(',').map((x) => x?.trim()))[0]
    let query = ''
    if (dryRun) {
      const selectQuery = dedent`
        SELECT
          *,
          ${variablePairs.map(([variable, aggregation]) => `0 AS ${variable}_${aggregation}`).join(',\n')}
        FROM
          ${connection.quoteName(prepParams.target as string)}`
      query = connection.getCreateTempTableQuery(outputTablename, selectQuery, codeType, null)
      query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)
    } else {
      const excludeVars = variablePairs.filter(([variable, aggregation]) =>
        ['max', 'min', 'count'].includes(aggregation.toLowerCase())
      )
      const includeVars = variablePairs.filter(
        ([variable, aggregation]) => !['max', 'min', 'count'].includes(aggregation.toLowerCase())
      )

      if (includeVars.length < 1) {
        throw new Error('Number of variables zero')
      }

      // Set temporary table names
      const tempTableMelt = outputTablename + '_temp_melt'
      const tempTableMeltWithBboxTotal = outputTablename + '_temp_melt_bbox_total'
      const tempTableMeltWithBboxIntersection = outputTablename + '_temp_melt_bbox_intersection'
      const tempTableEnrichedIntersection = outputTablename + '_temp_enriched_intersection'
      const tempTableEnrichedTotal = outputTablename + '_temp_enriched_total'
      const tempTableExcludedVars = outputTablename + '_temp_excluded_vars'
      const tempTablePreweights = outputTablename + '_temp_preweights'
      const tempTableSourceWithBbox = outputTablename + '_temp_source_bbox'
      const tempTableTargetWithBbox = outputTablename + '_temp_target_bbox'
      const tempTableWeightsWithBbox = outputTablename + '_temp_weights_bbox'

      const vars = [...new Set(includeVars.map(([variable, aggregation]) => variable))]

      const setClusterParamsQuery = getSetClusterParamsQuery(connection, [
        tempTableSourceWithBbox,
        tempTableTargetWithBbox
      ])
      const createSourceTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        prepParams.source as string,
        tempTableSourceWithBbox,
        prepParams.source_col as string,
        (x) => `ST_GEOMFROMWKB(${x})`
      )
      const createTargetTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        prepParams.target as string,
        tempTableTargetWithBbox,
        target_col as string,
        (x) => `ST_GEOMFROMWKB(${x})`
      )
      const createWeightsTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        prepParams.weights_table as string,
        tempTableWeightsWithBbox,
        prepParams.weights_col as string,
        (x) => `ST_GEOMFROMWKB(${x})`
      )
      let createExcludeVariableTablesQuery = ''
      // Handle excluded variables
      if (excludeVars.length > 0) {
        const selectCreateExcludeVariablesTable = this._getEnrichedPolygonsQuery(
          tempTableSourceWithBbox,
          prepParams.source_col as string,
          tempTableTargetWithBbox,
          target_col as string,
          prepParams.target as string,
          excludeVars,
          connection as DBricksConnection
        )
        createExcludeVariableTablesQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
          connection.getCreateTempTableQuery(tempTableExcludedVars, selectCreateExcludeVariablesTable, codeType, null)
        )
      }

      const selectMeltTableQuery = dedent`
        WITH
        input_geoms AS (
          SELECT
            ${this.toGeomFunction(`ANY_VALUE(${target_col})`)} AS geom,
            ANY_VALUE(${target_col}) AS geom_org,
            ANY_VALUE(xmin) AS xmin,
            ANY_VALUE(xmax) AS xmax,
            ANY_VALUE(ymin) AS ymin,
            ANY_VALUE(ymax) AS ymax
          FROM
            ${connection.quoteName(tempTableTargetWithBbox)}
          GROUP BY ${target_col}
        ),
        enrich_data AS (
          SELECT
            __carto_id,
            ${prepParams.source_col} AS geom,
            ${prepParams.source_col} AS geom_org,
            ${vars.join(',\n')},
            xmin,
            xmax,
            ymin,
            ymax
          FROM
            ${connection.quoteName(tempTableSourceWithBbox)}
        ),
        intersections AS (
          SELECT
            __carto_id,
            input_geoms.geom,
            input_geoms.geom_org,
            enrich_data.geom as geom_enrich,
            enrich_data.geom_org as geom_enrich_org,
            ${vars.join(',\n')},
            ST_INTERSECTION(input_geoms.geom, enrich_data.geom) AS __carto_intersection_geom
          FROM
            input_geoms,
            enrich_data
          WHERE
            (
              input_geoms.xmax >= enrich_data.xmin
              AND input_geoms.xmin <= enrich_data.xmax
              AND input_geoms.ymax >= enrich_data.ymin
              AND input_geoms.ymin <= enrich_data.ymax
            )
          AND
            ST_INTERSECTS(input_geoms.geom, enrich_data.geom)
        )
        ${vars
          .map(
            (v) => dedent`
                      SELECT
                        '${v}' AS variable,
                        ${v} AS value,
                        ST_ASEWKB(__carto_intersection_geom) AS __carto_intersection_geom,
                        geom_enrich_org AS __carto_total_geom,
                        geom_org AS ${target_col}
                      FROM intersections
                      `
          )
          .join('UNION ALL\n')}
        `
      const createMeltTableQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        connection.getCreateTempTableQuery(tempTableMelt, selectMeltTableQuery, codeType, null)
      )

      const createMeltProcessedTotalTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        tempTableMelt,
        tempTableMeltWithBboxTotal,
        '__carto_total_geom',
        (x) => `ST_GEOMFROMWKB(${x})`
      )

      const createMeltProcessedIntersectionTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        tempTableMelt,
        tempTableMeltWithBboxIntersection,
        '__carto_intersection_geom',
        (x) => `ST_GEOMFROMWKB(${x})`
      )

      const selectEnrichedTotalTable = this._getEnrichedPolygonsQuery(
        tempTableWeightsWithBbox,
        prepParams.weights_col as string,
        tempTableMeltWithBboxTotal,
        '__carto_total_geom',
        tempTableMelt,
        [weight],
        connection as DBricksConnection
      )
      const createEnrichedTotalTableQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        connection.getCreateTempTableQuery(tempTableEnrichedTotal, selectEnrichedTotalTable, codeType, null)
      )

      const selectEnrichedIntersectionTable = this._getEnrichedPolygonsQuery(
        tempTableWeightsWithBbox,
        prepParams.weights_col as string,
        tempTableMeltWithBboxIntersection,
        '__carto_intersection_geom',
        tempTableMelt,
        [weight],
        connection as DBricksConnection
      )
      const createEnrichedIntersectionTableQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        connection.getCreateTempTableQuery(
          tempTableEnrichedIntersection,
          selectEnrichedIntersectionTable,
          codeType,
          null
        )
      )

      const weightedCalcQuery = dedent`
        spark.sql('''
          CREATE TABLE IF NOT EXISTS ${connection.quoteName(tempTablePreweights)} AS
            SELECT
              a.${target_col},
              a.variable AS variable,
              a.value,
              a.${weight[0]}_${weight[1]} AS intersection_metric,
              b.${weight[0]}_${weight[1]} AS total_metric,
              c.aggregation
            FROM ${connection.quoteName(tempTableEnrichedIntersection)} a
            JOIN ${connection.quoteName(tempTableEnrichedTotal)} b
            ON (
              a.${target_col} = b.${target_col}
              AND
              a.variable = b.variable
            )
            JOIN (${includeVars
              .map(([variable, aggregation]) => `SELECT '${variable}' AS variable, '${aggregation}' AS aggregation`)
              .join(' UNION ALL ')}
            ) c
            ON a.variable = c.variable
        '''
        )
        `

      const selectFinalEnrichmentQuery = dedent`
          WITH agg AS (
              SELECT
                ANY_VALUE(${target_col})
                  AS ${target_col},
                variable, aggregation,
                SUM(IFF(intersection_metric = 0, NULL, intersection_metric)) AS total_intersection
              FROM ${connection.quoteName(tempTablePreweights)} preweights
              GROUP BY ${target_col}, variable, aggregation
          ),
          tmp AS (
              SELECT
                preweights.${target_col} AS geom,
                CONCAT(preweights.variable, '_', preweights.aggregation) AS variable,
                CASE
                    WHEN LOWER(preweights.aggregation) IN ('sum', 'count') THEN SUM(COALESCE(IFF(intersection_metric = 0 OR total_metric = 0, NULL, intersection_metric / total_metric), 0) * CAST(value AS FLOAT))
                    WHEN LOWER(preweights.aggregation) = 'min' THEN MIN(value)
                    WHEN LOWER(preweights.aggregation) = 'max' THEN MAX(value)
                    WHEN LOWER(preweights.aggregation) = 'avg' THEN SUM(COALESCE(IFF(intersection_metric = 0 OR total_intersection = 0, NULL, intersection_metric / total_intersection), 0) * CAST(value AS FLOAT))
                END AS value
              FROM ${connection.quoteName(tempTablePreweights)} preweights
              JOIN agg
              ON (
                preweights.${target_col} = agg.${target_col}
                AND preweights.variable = agg.variable
                AND preweights.aggregation = agg.aggregation)
              GROUP BY
                preweights.${target_col},
                preweights.variable,
                preweights.aggregation
          ),
          datatemp AS (
              SELECT *
              FROM tmp
              PIVOT(SUM(value) FOR variable IN (
                ${includeVars.map(([variable, aggregation]) => `'${variable}_${aggregation}'`).join(', ')})
              )
          )
          SELECT
            input_query.*,
            ${includeVars
              .map(([variable, aggregation]) => `datatemp.${variable}_${aggregation} AS ${variable}_${aggregation}`)
              .join(', ')}
          FROM datatemp
          RIGHT JOIN ${connection.quoteName(prepParams.target as string)} AS input_query
          ON datatemp.geom = input_query.${target_col}
          `

      const createFinalEnrichmentTableQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        connection.getCreateTempTableQuery(outputTablename, selectFinalEnrichmentQuery, codeType, null)
      )

      query = dedent`
        # Add a custom function to get the geometry from the source table
        ${this._getCreateGetDimensionFunction()}
        # Prepare tables with bounding boxes
        ${createSourceTableQuery}
        ${createTargetTableQuery}
        ${createWeightsTableQuery}
        ${createExcludeVariableTablesQuery}
        ${setClusterParamsQuery}
        # Melt the target table
        ${createMeltTableQuery}
        # Create melt table with bbox for total geometry
        ${createMeltProcessedTotalTableQuery}
        # Create melt table with bbox for intersection geometry
        ${createMeltProcessedIntersectionTableQuery}
        # Enrich the total geometry with the weights table
        ${createEnrichedTotalTableQuery}
        # Enrich the intersection geometry with the weights table
        ${createEnrichedIntersectionTableQuery}
        # Calculate the preweights
        ${weightedCalcQuery}
        # Create the final enrichment table
        ${createFinalEnrichmentTableQuery}
      `
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }
}

export { EnrichPolygonsWeightedDatabricks }
